import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, BarChart3 } from 'lucide-react';
import { haalBedrijfscijfers, type FiscaleMatrix, type Granulariteit, type RolFilter, type RolGroep } from '../../services/professionalDataApi';
import { Kaart, KpiTegel, Laden } from '../data/DataFilterBalk';
import { Legenda, REEKS_KLEUREN, euro } from '../data/DataCharts';
import { StapelMetVorigJaar } from './Grafieken';
import { FilterRij, Fout, Keuze, Knoppenbalk, jaarOpties } from './Bediening';

/** Kleur volgt de rol, nooit de rangorde: VTP onder, BMW erboven, overig bovenop. */
export const ROL_KLEUR: Record<RolGroep, string> = { VTP: REEKS_KLEUREN[0], BMW: REEKS_KLEUREN[1], OVERIG: REEKS_KLEUREN[2] };

const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

const Verschil: React.FC<{ nu: number; toen: number }> = ({ nu, toen }) => {
  if (toen === 0) return <span className="text-gray-400">—</span>;
  const p = ((nu - toen) / toen) * 100;
  return <span className={p >= 0 ? 'text-green-800' : 'text-red-700'}>{`${p >= 0 ? '+' : ''}${p.toLocaleString('nl-NL', { maximumFractionDigits: 1 })}%`}</span>;
};

export const BedrijfscijfersOnderdeel: React.FC = () => {
  const { t } = useTranslation('professionalData');
  const nu = new Date();
  const [jaar, setJaar] = useState(nu.getFullYear());
  const [granulariteit, setGranulariteit] = useState<Granulariteit>('jaar');
  const [maand, setMaand] = useState(nu.getMonth() + 1);
  const [kwartaal, setKwartaal] = useState(Math.floor(nu.getMonth() / 3) + 1);
  const [vergelijk, setVergelijk] = useState(false);
  const [rol, setRol] = useState<RolFilter>('ALLE');
  const deel = granulariteit === 'maand' ? maand : granulariteit === 'kwartaal' ? kwartaal : 1;

  const q = useQuery({
    queryKey: ['professional-data', 'bedrijfscijfers', jaar, granulariteit, deel, rol, vergelijk],
    queryFn: () => haalBedrijfscijfers({ jaar, granulariteit, deel, rol, vergelijk }),
    placeholderData: keepPreviousData,
  });
  const d = q.data;

  const periodeLabel = granulariteit === 'maand' ? `${MAANDEN[maand - 1]} ${jaar}` : granulariteit === 'kwartaal' ? `Q${kwartaal} ${jaar}` : String(jaar);
  const rolLabel = (r: RolGroep) => (r === 'OVERIG' ? t('bedrijfscijfers.overig') : r);
  const reeksen = (['VTP', 'BMW', 'OVERIG'] as RolGroep[])
    .filter((r) => (rol === 'ALLE' ? r !== 'OVERIG' || d?.maanden.some((m) => m.OVERIG > 0) : r === rol))
    .map((r) => ({ key: r, label: rolLabel(r), kleur: ROL_KLEUR[r] }));

  const lagen: { key: keyof Pick<FiscaleMatrix, 'netto' | 'btw' | 'bruto'>; label: string; desc: string }[] = [
    { key: 'netto', label: t('bedrijfscijfers.laagA'), desc: t('bedrijfscijfers.laagADesc') },
    { key: 'btw', label: t('bedrijfscijfers.laagB'), desc: t('bedrijfscijfers.laagBDesc') },
    { key: 'bruto', label: t('bedrijfscijfers.laagC'), desc: t('bedrijfscijfers.laagCDesc') },
  ];

  return (
    <div className="space-y-6">
      <FilterRij>
        <Keuze label={t('bedrijfscijfers.jaar')} waarde={jaar} onChange={setJaar} opties={jaarOpties()} />
        <Knoppenbalk
          label={t('bedrijfscijfers.kwartaal')}
          waarde={granulariteit}
          onChange={setGranulariteit}
          opties={[
            { waarde: 'maand', label: t('bedrijfscijfers.maand') },
            { waarde: 'kwartaal', label: t('bedrijfscijfers.kwartaal') },
            { waarde: 'jaar', label: t('bedrijfscijfers.heelJaar') },
          ]}
        />
        {granulariteit === 'maand' && (
          <Keuze label={t('bedrijfscijfers.maand')} waarde={maand} onChange={setMaand} opties={MAANDEN.map((m, i) => ({ waarde: i + 1, label: m }))} />
        )}
        {granulariteit === 'kwartaal' && (
          <Keuze label={t('bedrijfscijfers.kwartaal')} waarde={kwartaal} onChange={setKwartaal} opties={[1, 2, 3, 4].map((k) => ({ waarde: k, label: `Q${k}` }))} />
        )}
        <button
          type="button"
          aria-pressed={vergelijk}
          onClick={() => setVergelijk((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${vergelijk ? 'border-navy-900 bg-navy-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-navy-300'}`}
        >
          <BarChart3 className="h-4 w-4" />
          {t('bedrijfscijfers.vergelijk')}
        </button>
        <Keuze
          label={t('bedrijfscijfers.rol')}
          waarde={rol}
          onChange={setRol}
          opties={[
            { waarde: 'ALLE', label: t('bedrijfscijfers.rolAlle') },
            { waarde: 'VTP', label: t('bedrijfscijfers.rolVTP') },
            { waarde: 'BMW', label: t('bedrijfscijfers.rolBMW') },
          ]}
        />
      </FilterRij>

      {q.isLoading && <Laden />}
      {q.isError && <Fout fout={q.error} />}
      {d && (
        <>
          <Kaart titel={t('bedrijfscijfers.matrixTitel', { periode: periodeLabel })} uitleg={t('bedrijfscijfers.matrixUitleg')}>
            <div className="grid gap-4 md:grid-cols-3">
              {lagen.map((l) => (
                <div key={l.key} className="rounded-lg border border-gray-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-navy-900">{l.label}</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">{euro(d.matrix[l.key], 2)}</p>
                  <p className="text-xs text-gray-500">{l.desc}</p>
                  {d.vergelijking && (
                    <p className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-600">
                      {t('bedrijfscijfers.vorigJaar')}: <strong className="text-gray-800">{euro(d.vergelijking.matrix[l.key], 2)}</strong> ·{' '}
                      {t('bedrijfscijfers.verschil')} <Verschil nu={d.matrix[l.key]} toen={d.vergelijking.matrix[l.key]} />
                    </p>
                  )}
                </div>
              ))}
            </div>

            {d.matrix.vrijgesteld.medisch + d.matrix.vrijgesteld.kor > 0 && (
              <div className="mt-4 rounded-lg border border-navy-100 bg-navy-50/40 p-3 text-sm text-gray-800">
                <p className="font-semibold text-navy-900">{t('bedrijfscijfers.vrijgesteldTitel')}</p>
                <p className="mt-1">
                  {t('bedrijfscijfers.vrijgesteldTekst', { belast: euro(d.matrix.belast_netto, 2) })}
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
                  {d.matrix.vrijgesteld.medisch > 0 && <li>{t('bedrijfscijfers.vrijgesteldMedisch', { netto: euro(d.matrix.vrijgesteld.medisch, 2) })}</li>}
                  {d.matrix.vrijgesteld.kor > 0 && <li>{t('bedrijfscijfers.vrijgesteldKor', { netto: euro(d.matrix.vrijgesteld.kor, 2) })}</li>}
                </ul>
              </div>
            )}

            {d.matrix.arbo_vrijgesteld.netto > 0 && (
              <div className="mt-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="flex items-center gap-2 font-semibold">
                  <AlertTriangle className="h-4 w-4" aria-hidden />
                  {t('bedrijfscijfers.arboTitel')}
                </p>
                <p className="mt-1">{t('bedrijfscijfers.arboTekst', { netto: euro(d.matrix.arbo_vrijgesteld.netto, 2) })}</p>
                {d.matrix.arbo_vrijgesteld.btw > 0 && <p className="mt-1 text-xs">{t('bedrijfscijfers.arboBtw', { btw: euro(d.matrix.arbo_vrijgesteld.btw, 2) })}</p>}
              </div>
            )}

            {rol === 'ALLE' && (
              <div className="mt-5 overflow-x-auto">
                <p className="mb-2 text-sm font-semibold text-gray-900">{t('bedrijfscijfers.perRolTitel')}</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                      <th className="py-2 pr-3 font-medium">{t('bedrijfscijfers.rolKolom')}</th>
                      <th className="py-2 pr-3 text-right font-medium">{t('bedrijfscijfers.nettoKolom')}</th>
                      <th className="py-2 pr-3 text-right font-medium">{t('bedrijfscijfers.btwKolom')}</th>
                      <th className="py-2 text-right font-medium">{t('bedrijfscijfers.brutoKolom')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.per_rol.map((r) => (
                      <tr key={r.rol} className="border-b border-gray-100">
                        <td className="min-w-[10rem] py-2 pr-3">
                          <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: ROL_KLEUR[r.rol] }} />
                          {t(`bedrijfscijfers.rolNaam.${r.rol}`)}
                        </td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">{euro(r.netto, 2)}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">{euro(r.btw, 2)}</td>
                        <td className="py-2 text-right whitespace-nowrap font-medium">{euro(r.bruto, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Kaart>

          <div>
            <h2 className="mb-3 text-base font-semibold text-gray-900">{t('bedrijfscijfers.cashflowTitel')}</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <KpiTegel label={t('bedrijfscijfers.gerealiseerd')} waarde={euro(d.cashflow.gerealiseerd, 2)} sub={t('bedrijfscijfers.gerealiseerdDesc')} />
              <KpiTegel
                label={t('bedrijfscijfers.pending')}
                waarde={euro(d.cashflow.pending, 2)}
                sub={t('bedrijfscijfers.pendingDesc', { fiat: euro(d.cashflow.pending_fiat, 2), uitbetaling: euro(d.cashflow.pending_uitbetaling, 2) })}
              />
              <KpiTegel label={t('bedrijfscijfers.prognose')} waarde={euro(d.cashflow.prognose, 2)} sub={t('bedrijfscijfers.prognoseDesc')} />
            </div>
          </div>

          <Kaart titel={t('bedrijfscijfers.grafiekTitel')} uitleg={t('bedrijfscijfers.grafiekUitleg', { jaar: d.jaar, vorig: d.jaar - 1 })}>
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              <Legenda items={reeksen.map((r) => ({ label: r.label, kleur: r.kleur }))} />
              <span className="flex items-center gap-1.5 text-xs text-gray-600">
                <svg width="18" height="6" aria-hidden><line x1="0" y1="3" x2="18" y2="3" stroke="#0B1F3A" strokeWidth="1.5" strokeDasharray="5 3" /></svg>
                {t('bedrijfscijfers.lijnVorigJaar', { jaar: d.jaar - 1 })}
              </span>
            </div>
            <StapelMetVorigJaar
              maanden={d.maanden.map((m) => m.maand)}
              reeksen={reeksen}
              waarden={d.maanden.map((m) => ({ VTP: m.VTP, BMW: m.BMW, OVERIG: m.OVERIG }))}
              vorigJaar={d.vorig_jaar}
              vorigJaarLabel={t('bedrijfscijfers.lijnVorigJaar', { jaar: d.jaar - 1 })}
            />
          </Kaart>
        </>
      )}
    </div>
  );
};
